/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {m, p} from 'styled-components-spacing';
import {isList, ResponsiveSpaceProp, spacingPropTypeMap} from '../utils';

import {margins} from './lib/margin';
import {paddings} from './lib/padding';
import {display, displayPropTypeMap, DisplayType} from './lib/display';
import {width, maxWidth, height, maxHeight, dimensionPropTypeMap, DimensionsType} from './lib/dimensions';
import {
  alignItems,
  alignItemsPropTypeMap,
  justifyContent,
  justifyContentPropTypeMap,
  flexDirection,
  flexDirectionPropTypeMap,
  flexWrap,
  flexWrapPropTypeMap,
  flexGrow,
  flexGrowPropTypeMap,
  flexShrink,
  flexShrinkPropTypeMap,
  AlignItemsType,
  JustifyContentType,
  flexDirectionValues,
  FlexWrapType,
  FlexGrowShrinkType
} from './lib/flex';
import {textAlign, textAlignPropTypeMap, TextAlignType} from './lib/textAlign';
import {background, color, colorsPropType, colorValues} from './lib/colors';
import {borderRadius, borderRadiusPropTypeMap, borderRadiusValues} from './lib/border-radius';
import {boxShadow, boxShadowPropTypeMap, boxShadowValues} from './lib/box-shadow';

export interface BoxProps {
  padding?: ResponsiveSpaceProp;
  paddingVertical?: ResponsiveSpaceProp;
  paddingHorizontal?: ResponsiveSpaceProp;
  paddingTop?: ResponsiveSpaceProp;
  paddingBottom?: ResponsiveSpaceProp;
  paddingLeft?: ResponsiveSpaceProp;
  paddingRight?: ResponsiveSpaceProp;
  margin?: ResponsiveSpaceProp;
  marginVertical?: ResponsiveSpaceProp;
  marginHorizontal?: ResponsiveSpaceProp;
  marginTop?: ResponsiveSpaceProp;
  marginBottom?: ResponsiveSpaceProp;
  marginLeft?: ResponsiveSpaceProp;
  marginRight?: ResponsiveSpaceProp;
  display?: DisplayType;
  width?: DimensionsType;
  maxWidth?: DimensionsType;
  height?: DimensionsType;
  maxHeight?: DimensionsType;
  alignItems?: AlignItemsType;
  justifyContent?: JustifyContentType;
  flexDirection?: flexDirectionValues;
  flexWrap?: FlexWrapType;
  flexGrow?: FlexGrowShrinkType;
  flexShrink?: FlexGrowShrinkType;
  textAlign?: TextAlignType;
  background?: colorValues;
  color?: colorValues;
  borderRadius?: borderRadiusValues;
  boxShadow?: boxShadowValues;
  as?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

export const Box = styled(
  ({
    padding,
    paddingVertical,
    paddingHorizontal,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginVertical,
    marginHorizontal,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    display,
    width,
    maxWidth,
    height,
    maxHeight,
    alignItems,
    justifyContent,
    flexDirection,
    flexWrap,
    flexGrow,
    flexShrink,
    textAlign,
    background,
    color,
    borderRadius,
    boxShadow,
    as,
    ...rest
  }) => <div {...rest} />
)<BoxProps>`
  box-sizing: border-box;

  // If box is set to be a ul or ol we need to reset the default styling
  ${({as}) =>
    as &&
    isList(as) &&
    css`
      ${m(0)};
      ${p(0)};
      list-style-type: none;
    `}

  ${props => margins(props)};
  ${props => paddings(props)};

  ${props => display(props)};

  ${props => width(props)};
  ${props => maxWidth(props)};
  ${props => height(props)};
  ${props => maxHeight(props)};

  ${props => alignItems(props)};
  ${props => justifyContent(props)};
  ${props => flexDirection(props)};
  ${props => flexWrap(props)};
  ${props => flexGrow(props)};
  ${props => flexShrink(props)};

  ${props => textAlign(props)};

  ${props => background(props)};
  ${props => color(props)};
  ${props => borderRadius(props)};
  ${props => boxShadow(props)};
`;

Box.displayName = 'Box';

Box.propTypes = {
  padding: spacingPropTypeMap,
  paddingVertical: spacingPropTypeMap,
  paddingHorizontal: spacingPropTypeMap,
  paddingTop: spacingPropTypeMap,
  paddingBottom: spacingPropTypeMap,
  paddingLeft: spacingPropTypeMap,
  paddingRight: spacingPropTypeMap,

  margin: spacingPropTypeMap,
  marginVertical: spacingPropTypeMap,
  marginHorizontal: spacingPropTypeMap,
  marginTop: spacingPropTypeMap,
  marginBottom: spacingPropTypeMap,
  marginLeft: spacingPropTypeMap,
  marginRight: spacingPropTypeMap,

  display: displayPropTypeMap,
  width: dimensionPropTypeMap,
  maxWidth: dimensionPropTypeMap,
  height: dimensionPropTypeMap,
  maxHeight: dimensionPropTypeMap,

  alignItems: alignItemsPropTypeMap,
  justifyContent: justifyContentPropTypeMap,
  flexDirection: flexDirectionPropTypeMap,
  flexWrap: flexWrapPropTypeMap,
  flexGrow: flexGrowPropTypeMap,
  flexShrink: flexShrinkPropTypeMap,

  textAlign: textAlignPropTypeMap,

  background: colorsPropType,
  color: colorsPropType,
  borderRadius: borderRadiusPropTypeMap,
  boxShadow: boxShadowPropTypeMap,

  as: PropTypes.string,
  children: PropTypes.node
};
